import { Injectable, inject } from '@angular/core';

import { ONBOARDING_ENABLED } from './app-config';
import { DesignsFacade } from '../features/designs/domain/application/designs.facade';
import { ProjectsFacade } from '../features/projects/domain/application/projects.facade';
import type { FC_ProjectDTO } from '../features/projects/domain/entities/project.models';
import { isDemoProject } from '../features/projects/domain/utils/project.utils';
import { withTimeout } from '../features/projects/domain/utils/with-timeout.util';
import { LIST_READ_LIMIT_MS } from './route-wait';

/** Where the gate resumes a not-onboarded user in the `/onboarding` wizard. */
export interface OnboardingResumeTarget {
  step: number;
  projectId?: string;
  /** The approved design pack the review step must poll — without it the wizard opens a fresh empty draft. */
  designId?: string;
  resumeSuccess?: boolean;
  /** Which wizard hosts the resume. Absent/'onboarding' → the glued first-time `/onboarding` funnel;
   *  'projectWizard' → the standalone «Создать проект» wizard (`projects/new`) for a subsequent
   *  album-less project on an account that already finished onboarding. */
  surface?: 'onboarding' | 'projectWizard';
  /** Which onboarding configuration the resume lands in: 'upload' when the account owns an approved
   *  uploaded design (the two-phase PSD ladder), 'select' otherwise (template-first). */
  mode?: 'select' | 'upload';
  /** The target project's name — the standalone wizard's page header on resume (`?name=`). */
  name?: string;
}

export interface OnboardingResume {
  onboarded: boolean;
  target: OnboardingResumeTarget;
}

/** What is known about ONE project's own album. `unknown` is not a licence to delete. */
export type AlbumVerdict = 'album-free' | 'has-album' | 'unknown';

/**
 * A finished album is the terminal signal — until then the furthest project step maps to the mode's
 * onboarding scale: UPLOAD (two-phase) — 0-based project step + 4, capped at 7; SELECT
 * (single-phase default) — + 2, capped at 5. The cap is the removed «Проверка персон» slot still
 * stored in old projects; the onboarding page lands it on the last photo step. The processing clamp
 * runs on the onboarding page, not here: the guard must stay fast and never block on a status poll.
 */
function deriveProjectStep(project: FC_ProjectDTO, upload: boolean): number {
  const wizardStep = project.settings?.data?.lastWizardStep;
  const base = upload ? 4 : 2;
  return wizardStep == null ? base : Math.min(wizardStep + base, base + 3);
}

/**
 * Standalone «Создать проект» wizard scale (1-4, «Портреты учеников» first — the template is picked in
 * the create-project modal, so the wizard carries no «Выбор шаблона» step): the persisted 0-based
 * project step + 1, capped at 4 — the removed «Проверка персон» slot, which the wizard lands on the last
 * photo step; that step's CTA generates the first album and thereby lifts the gate entirely. The cap
 * mirrors deriveProjectStep.
 */
function deriveStandaloneProjectStep(project: FC_ProjectDTO): number {
  const wizardStep = project.settings?.data?.lastWizardStep;
  return wizardStep == null ? 1 : Math.min(wizardStep + 1, 4);
}

@Injectable({ providedIn: 'root' })
export class OnboardingStateService {
  private readonly designs = inject(DesignsFacade);
  private readonly projects = inject(ProjectsFacade);

  // Concurrent cabinet navigations share one data load; invalidate() drops it so the next navigation
  // refetches (after logout, or once the user crosses the album threshold). Only the load is cached —
  // the gate verdict is per-project, so decide() is recomputed per call from the target id.
  private inFlight: Promise<boolean> | null = null;
  private loadedOnce = false;

  resolve(targetProjectId?: string): Promise<OnboardingResume | null> {
    // The kill-switch rides here, not in the guard: `null` is the established "no verdict" value all
    // three call sites already fail open on, so one flag lifts the gate everywhere without touching them.
    // OFF retires the ACCOUNT-WIDE funnel only — registration lands straight in the cabinet and no
    // wizard is ever forced on a user who did not ask for one. Opening a project that never finished
    // creation (no album of its own) is exactly such an ask: it keeps resuming that project's own
    // «Создать проект» wizard at the step it was left on, so an OFF switch answers blind only when
    // there is no target project, and otherwise passes everything but a standalone-wizard verdict.
    if (!ONBOARDING_ENABLED && !targetProjectId) {
      return Promise.resolve(null);
    }

    const fresh = (this.inFlight ??= this.load()).then(loaded => (loaded ? this.gated(this.decide(targetProjectId)) : null));
    // Revisit after invalidate(): a pass read off the lists already loaded answers now, the refetch runs
    // behind it. Albums only grow a pass; a redirect off stale lists could trap an onboarded user, so it waits.
    if (this.loadedOnce) {
      const known = this.decide(targetProjectId);
      if (known.onboarded) return Promise.resolve(this.gated(known));
    }

    return fresh;
  }

  /**
   * Whether THIS project owns an album — the question a destructive action needs, which decide()
   * cannot answer: an album-free demo target falls back to an account-wide verdict whose projectId
   * belongs to someone else, so a missing match there means «no answer», not «album-free».
   * The kill-switch is deliberately not consulted: it lifts the gate, it does not change what a
   * project contains.
   */
  hasAlbum(projectId: string): Promise<AlbumVerdict> {
    if (!projectId) return Promise.resolve('unknown');
    return (this.inFlight ??= this.load()).then(loaded => {
      if (!loaded) return 'unknown';
      const target = this.projects.knownProjects().find(project => project.id === projectId);
      if (!target) return 'unknown';
      return (target.counter?.photobooks ?? 0) > 0 ? 'has-album' : 'album-free';
    });
  }

  invalidate(): void {
    this.inFlight = null;
  }

  private gated(resume: OnboardingResume): OnboardingResume | null {
    return ONBOARDING_ENABLED || resume.target.surface === 'projectWizard' ? resume : null;
  }

  private load(): Promise<boolean> {
    const load: Promise<boolean> = withTimeout(
      // «Архив» rows decide too, and the list read only kicks that shelf: wait for that same read.
      Promise.all([this.designs.loadDesigns(), this.projects.loadProjects().then(() => this.projects.joinPutAwayKick())]),
      LIST_READ_LIMIT_MS,
    ).then(
      () => (this.loadedOnce = true),
      () => {
        // Fail open — a network blip or a silent server must never trap a user out of the cabinet.
        // Only this load's own slot is cleared: after invalidate() a newer load may already hold it.
        if (this.inFlight === load) this.inFlight = null;
        return false;
      },
    );
    return load;
  }

  private decide(targetProjectId?: string): OnboardingResume {
    // «Архив» rows count too: a put-away project keeps its album and its unfinished wizard.
    const projects = this.projects.knownProjects();
    // The account-level onboarding signal (any produced album) — also decides WHICH wizard resumes a
    // specific album-less project below, so compute it once up front.
    const projectWithAlbum = projects.find(project => !isDemoProject(project) && (project.counter?.photobooks ?? 0) > 0);
    const target = targetProjectId ? projects.find(project => project.id === targetProjectId) : undefined;

    // A specific project is gated on ITS OWN album, not the account's — demo included: a demo album
    // never onboards the ACCOUNT (projectWithAlbum above excludes demo), but the demo project itself,
    // opened by id, owns its album and must open. Without this branch the gate would swap the freshly
    // generated demo album's editor navigation for whatever other project happens to be resumable.
    if (target && (target.counter?.photobooks ?? 0) > 0) {
      return { onboarded: true, target: { step: 8 } };
    }

    // An album-less non-demo target always resumes its wizard even when another project already
    // finished onboarding on this account.
    if (target && !isDemoProject(target)) {
      // WHERE it resumes depends on the account: an already-onboarded account finishes a SUBSEQUENT
      // project in its own standalone «Создать проект» wizard (no design steps, resumed at the exact
      // persisted step); a not-yet-onboarded account is still inside the glued `/onboarding` funnel.
      // With the funnel retired (kill-switch OFF) that glued tunnel no longer exists for anyone, so
      // every unfinished project — first one included — resumes in the standalone wizard.
      return projectWithAlbum || !ONBOARDING_ENABLED
        ? {
            onboarded: false,
            target: {
              projectId: target.id,
              step: deriveStandaloneProjectStep(target),
              surface: 'projectWizard',
              ...(target.name ? { name: target.name } : {}),
            },
          }
        : { onboarded: false, target: this.onboardingProjectTarget(target) };
    }

    if (projectWithAlbum) {
      return { onboarded: true, target: { step: 8 } };
    }

    const resumableProject = projects.find(project => !isDemoProject(project));
    if (resumableProject) {
      return { onboarded: false, target: this.onboardingProjectTarget(resumableProject) };
    }

    const approvedDesignId = this.designs.latestApprovedDesignId();
    if (approvedDesignId) {
      return { onboarded: false, target: { step: 3, designId: approvedDesignId, resumeSuccess: true, mode: 'upload' } };
    }

    return { onboarded: false, target: { step: 1 } };
  }

  /** An approved uploaded design marks the account as an UPLOAD-mode tunnel (its project phase sits
   *  +4 and generation must get that design back); everyone else resumes the SELECT default. The
   *  project's OWN stored choice outranks that account-global guess — a project switched back to the
   *  catalog must not be resumed into the UPLOAD tunnel and overwritten with the approved pack. */
  private onboardingProjectTarget(project: FC_ProjectDTO): OnboardingResumeTarget {
    const approvedDesignId = this.designs.latestApprovedDesignId();
    const stored = project.settings?.data?.wizardSelection;
    const uploadDesignId = stored
      ? stored.mode === 'upload'
        ? (stored.uploadDesignId ?? approvedDesignId)
        : null
      : approvedDesignId;
    // A pack still with the designer has NOT earned the project phase, even when the project already
    // exists (the free-design escape creates one while the pack waits). Resume on its waiting screen —
    // where the escape back into free design lives — instead of letting the album be assembled from an
    // unapproved pack. An id the grid's first page does not know fails open, as everywhere else here.
    const stillWithDesigner =
      !!uploadDesignId && this.designs.designs().some(design => design.id === uploadDesignId && !design.approved);
    return {
      projectId: project.id,
      step: stillWithDesigner ? 3 : deriveProjectStep(project, !!uploadDesignId),
      mode: uploadDesignId ? 'upload' : 'select',
      ...(uploadDesignId ? { designId: uploadDesignId } : {}),
    };
  }
}
